import { useEffect, useState } from 'react';
import axios from 'axios';

/**
 * Whose careers page this is, for the bar across the top of it.
 *
 * Asked by the page's own address: an organization's slug, or a job id for the
 * pages that are about one job. The bars sit outside the pages they frame, so each
 * asks for itself, and the answers are kept for the length of the visit so a
 * person moving between the list, a job and its application is not made to wait
 * for the same logo three times.
 *
 * `status` is `loading`, `ready` or `none`. `none` is an address that names no
 * organization, which the bar answers by falling back to the product's own name
 * rather than an empty space.
 */
const answers = new Map();

const fetchBrand = ({ slug, jobId }) => {
  const key = slug ? `slug:${slug}` : `job:${jobId}`;
  if (!answers.has(key)) {
    const request = (slug
      ? axios.get(`/api/public/orgs/${encodeURIComponent(slug)}`).then((response) => ({
        name: response.data.name,
        slug: response.data.slug,
        logoUrl: response.data.logoUrl || null,
      }))
      : axios.get(`/api/public/jobs/${encodeURIComponent(jobId)}`).then((response) => ({
        name: response.data.organizationName,
        slug: response.data.careersSlug,
        logoUrl: response.data.logoUrl || null,
      }))
    ).catch((error) => {
      // A failure is not remembered, so the next visit tries again.
      answers.delete(key);
      throw error;
    });
    answers.set(key, request);
  }
  return answers.get(key);
};

/** For tests: forget everything that was fetched. */
export const forgetBrands = () => answers.clear();

const useCareersBrand = ({ slug, jobId } = {}) => {
  const [state, setState] = useState({ brand: null, status: slug || jobId ? 'loading' : 'none' });

  useEffect(() => {
    if (!slug && !jobId) {
      setState({ brand: null, status: 'none' });
      return undefined;
    }

    let current = true;
    setState({ brand: null, status: 'loading' });
    fetchBrand({ slug, jobId })
      .then((brand) => current && setState({ brand, status: 'ready' }))
      .catch(() => current && setState({ brand: null, status: 'none' }));

    return () => {
      current = false;
    };
  }, [slug, jobId]);

  return state;
};

export default useCareersBrand;
